/**
 * sessionStorage/localStorage相关操作
 * @author vincent.qiu
 */
import queryString from 'query-string';

import { supportLanguages } from '../constants';
import { domainReplace } from '../replaceDomain';
import { addCookie, getCookie } from './cookies';
import Guid from './guid';
import { hostMatch2, isMainNet } from './url';

const storage = {
  set(key: string, value: string) {
    if (window.localStorage) {
      if (value === undefined) {
        return localStorage.removeItem(key);
      }
      return localStorage.setItem(key, value);
    }
    return null;
  },
  /**
   * For sessionStorage
   */
  session: {
    set(key: string, value: string) {
      window.sessionStorage.setItem(key, value);
    },

    get(key: string) {
      return window.sessionStorage.getItem(key);
    },
  },
  get(key: string) {
    if (window.localStorage) {
      return localStorage.getItem(key);
    }
    return '';
  },

  remove(key: string) {
    if (window.localStorage) {
      localStorage.removeItem(key);
    }
  },
};

// 常量定义
export const BYBIT_LANG_KEY = 'BYBIT_LANG_KEY';
export const BYBIT_THEME_KEY = 'BYBIT_THEME_KEY';
export const BYBIT_GUID_KEY = '_by_l_g_d';
export const BYBIT_TRACKING_KEY = 'by_track';
export const BYBIT_INSURANCE_ADS = 'BYBIT_INSURANCE_ADS2'; // 保险推广弹窗

// 避免重复上报
export const reportReferrerFlag = 'reportReferrerFlag';

// 不同环境，设置不同的token cookie key
let BYBIT_TOKEN_COOKIE_KEY = 'b_t_c_k';
let REG_REF_KEY = 'BYBIT_REG_REF';
if (!isMainNet) {
  BYBIT_TOKEN_COOKIE_KEY = `b_t_c_k_${hostMatch2}`;
  REG_REF_KEY = `${REG_REF_KEY}_${hostMatch2}`;
}

console.info(BYBIT_TOKEN_COOKIE_KEY, REG_REF_KEY, '------ cookie -----');

export function getRegParams() {
  const str = getCookie(REG_REF_KEY) as string;
  return JSON.parse(str) || {};
}

export function setRegParams(params = {}) {
  const { host } = window.location;
  const domain =
    host.indexOf('localhost') !== -1
      ? 'localhost'
      : host.replace(/^www|m|testnet/, '');
  addCookie(REG_REF_KEY, JSON.stringify(params), domain, '/', 43200); // 有效期 30天
}

export function setToken(token: string) {
  const { host } = window.location;
  const domain =
    host.indexOf('localhost') !== -1
      ? 'localhost'
      : host.replace(/^www|m|testnet/, '');
  addCookie(BYBIT_TOKEN_COOKIE_KEY, token, domain, '/', 10080); // 有效期 7天
}

export function getToken() {
  // v3 update for m站与pc站登录态打通
  return getCookie(BYBIT_TOKEN_COOKIE_KEY);
}

export function supportLang(lang: string) {
  // 屏蔽韩语
  // const langArr = ['en', 'zh-CN', 'zh-TW', 'ja-JP', 'ko-KR', 'ru-RU', 'es-ES', 'vi-VN', 'tr-TR', 'pt-BR', 'fr-FR', 'th-TH', 'id-ID'];
  const langArr = ['en', ...supportLanguages];
  return langArr.indexOf(lang) !== -1;
}
export function getLang() {
  let lang = '';
  let lLang = navigator.language;
  if (lLang === 'zh-MY') {
    lLang = 'zh-my';
  } else {
    lLang = 'en';
  }
  if (supportLang(lLang)) {
    lang = lLang;
  }
  const qsObj = queryString.parse(window.location.search);
  if (qsObj.language) {
    return qsObj.language;
  }
  if (window.localStorage) {
    const sLang = localStorage.getItem(BYBIT_LANG_KEY) || '';
    if (supportLang(sLang)) {
      lang = sLang;
    }
  }
  return lang || 'en';
}
export const getGoogleHelperUrl = () => {
  const lang = getLang();

  const helpDomain = domainReplace('help');

  let url = `${helpDomain}/hc/en-us/articles/900005776423-Losing-your-Bybit-Account-s-Google-Authenticator-GA-2FA`;
  switch (lang) {
    case 'zh-my':
      url = `${helpDomain}/hc/zh-my/articles/900005776423-%E4%B8%A2%E5%A4%B1%E6%82%A8%E7%9A%84Bybit%E8%B0%B7%E6%AD%8C%E8%BA%AB%E4%BB%BD%E9%AA%8C%E8%AF%81%E5%99%A8-GA-`;
      break;
    case 'zh-tw':
      url = `${helpDomain}/hc/zh-tw/articles/900005776423-%E4%B8%9F%E5%A4%B1%E6%82%A8%E7%9A%84Bybit%E7%A9%80%E6%AD%8C%E8%BA%AB%E4%BB%BD%E9%A9%97%E8%AD%89%E5%99%A8-GA-`;
      break;
    case 'id-id':
      url = `${helpDomain}/hc/id-id/articles/900005776423-Kehilangan-Autentikasi-Google-GA-2FA-Akun-Bybit-Anda`;
      break;
    case 'ja-jp':
      url = `${helpDomain}/hc/ja/articles/900005776423-Bybit%E3%82%A2%E3%82%AB%E3%82%A6%E3%83%B3%E3%83%88%E3%81%AEGoogle%E4%BA%8C%E6%AE%B5%E9%9A%8E%E8%AA%8D%E8%A8%BC%E3%82%B7%E3%82%B9%E3%83%86%E3%83%A0%E3%81%AE%E7%B4%9B%E5%A4%B1-2FA-`;
      break;
    case 'ko-kr':
      url = `${helpDomain}/hc/ko/articles/900005776423-Bybit-%EA%B3%84%EC%A0%95-%EA%B5%AC%EA%B8%80-OTP-Google-Authenticator-2FA-%EB%B6%84%EC%8B%A4`;
      break;
    case 'ru-ru':
      url = `${helpDomain}/hc/ru/articles/900005776423-%D0%9F%D0%BE%D1%82%D0%B5%D1%80%D1%8F%D0%BD-%D0%B4%D0%BE%D1%81%D1%82%D1%83%D0%BF-%D0%BA-%D0%B0%D0%BA%D0%BA%D0%B0%D1%83%D0%BD%D1%82%D1%83-%D0%B8%D0%B7-%D0%B7%D0%B0-Google-Authenticator-2FA`;
      break;
    case 'vi-vn':
      url = `${helpDomain}/hc/vi-vn/articles/900005776423-M%E1%BA%A5t-2FA-Google-Authenticator-GA-c%E1%BB%A7a-T%C3%A0i-kho%E1%BA%A3n-Bybit`;
      break;
    case 'en-us':
    case 'en':
    default:
      break;
  }
  return url;
};

/**
 * 本地缓存语言
 * @param language: (en|zh-MY|zh-HK)
 */
export function setLang(language: string) {
  if (window.localStorage && supportLang(language)) {
    localStorage.setItem(BYBIT_LANG_KEY, language);
  }
}

export function getGuid() {
  let guid = getCookie(BYBIT_GUID_KEY);
  if (!guid || guid.length !== 36) {
    guid = Guid();
  }
  const { host } = window.location;
  const domain =
    host.indexOf('localhost') !== -1
      ? 'localhost'
      : host.replace(/^www|m|testnet/, '');
  addCookie(BYBIT_GUID_KEY, guid, domain, '/', 172800);

  return guid;
}

export function getTheme() {
  if (window.localStorage) {
    return localStorage.getItem(BYBIT_THEME_KEY) || 'dark';
  }
  return 'dark';
}

export function setTheme(theme: string) {
  let themeStr = theme;
  if (themeStr !== 'dark') {
    themeStr = 'light';
  }
  if (window.localStorage) {
    localStorage.setItem(BYBIT_THEME_KEY, themeStr);
  }
}

export function setTrackingEvent(events: any) {
  if (window.localStorage) {
    localStorage.setItem(BYBIT_TRACKING_KEY, JSON.stringify(events));
  }
}

export function removeTrackingEvent() {
  if (window.localStorage) {
    localStorage.removeItem(BYBIT_TRACKING_KEY);
  }
}

export function getTrackingEvent() {
  if (window.localStorage) {
    const str = localStorage.getItem(BYBIT_TRACKING_KEY) as string;
    return JSON.parse(str) || [];
  }
  return [];
}

export default storage;
